// src/utils/sessionIdentity.js
//
// Session identity helpers (frontend)
// ===================================
//
// PURPOSE
// -------
// This module owns the smallest possible frontend persistence layer
// for tab-scoped session routing.
//
// In the hardened strict tab-selected model, this file stores ONLY:
//
//   1) tab-local sessionId  -> sent as X-Session-Id
//   2) tab-local userId     -> diagnostic helper / scoped logout support
//   3) tab-local tabId      -> diagnostics only
//
// Additionally, this file contains ONE important hardening mechanism:
//
//   4) runtime-owner detection for duplicate-tab repair
//
// WHY THIS EXTRA HARDENING EXISTS
// -------------------------------
// In theory, sessionStorage is tab-local.
// In practice, many browsers copy sessionStorage when the user does:
//
//   - Duplicate tab
//   - "Open current page in new tab" style flows
//   - browser restore / internal tab cloning scenarios
//
// That means a duplicated tab may incorrectly inherit:
//
//   - hb_session_id
//   - hb_session_user_id
//   - hb_tab_id
//
// Under your required security model, that is NOT acceptable.
//
// A duplicated tab must NOT silently inherit session authority.
// It must behave like a fresh unauthenticated tab and require login again.
//
// Therefore this file repairs cloned sessionStorage by:
// - detecting that the current JS runtime is not the original owner
// - generating a new tab id
// - stripping inherited session/user identity
//
// NOTHING ELSE
// ------------
// This file intentionally does NOT implement:
//
// - duplicate-tab recovery
// - parent/source tab inheritance
// - session registry in localStorage
// - lineage / family tracking
// - recovery hints
// - browser-wide session resurrection
// - "continue as current user" support
//
// SECURITY MODEL
// --------------
// The security posture is intentionally conservative:
//
//   THIS TAB may use ONLY the session selector explicitly stored in THIS TAB.
//
// So:
//
// - a fresh tab starts without local session authority
// - a duplicated tab starts without trusted local session authority
// - a manually opened URL starts without trusted local session authority
// - after local cleanup, this tab stays locally unauthenticated
//   until explicit login/bootstrap re-establishes session identity
//
// WHY THIS IS THE SAFEST VARIANT
// ------------------------------
// Your application must support:
//
// - many different users on one physical device
// - different users in different tabs
// - logout scoped to exactly one concrete session/tab context
//
// The safest frontend rule is therefore:
//
//   no guessing
//   no recovery
//   no shared fallback
//
// IMPORTANT NOTE
// --------------
// sessionId is NOT the real credential.
// It is only a routing / disambiguation selector for backend session resolution.
//
// Real authorization still depends on:
// - backend cookies
// - backend DB session validation
// - backend JWT validation
//
// STORAGE CHOICE
// --------------
// sessionStorage is used intentionally because:
// - it is tab-local
// - it fits the strict tab-selected model
// - it avoids browser-wide bleed-through between unrelated tabs/users
//

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Dev logging helpers                                                        */
/* -------------------------------------------------------------------------- */

function dlog(message, meta) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.log("[sessionIdentity]", message, meta || "");
}

function dwarn(message, meta) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.warn("[sessionIdentity]", message, meta || "");
}

function shortId(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

/* -------------------------------------------------------------------------- */
/* Storage keys                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Tab-local auth/session routing state.
 *
 * These values must remain in sessionStorage only.
 */
const SS_SESSION_ID = "hb_session_id";
const SS_USER_ID = "hb_session_user_id";

/**
 * Diagnostic-only tab id.
 *
 * IMPORTANT:
 * - not an auth credential
 * - not shared across tabs on purpose
 * - not used for backend auth
 * - useful for logs / troubleshooting
 */
const SS_TAB_ID = "hb_tab_id";

/**
 * Runtime-owner marker used to detect duplicated/cloned sessionStorage.
 *
 * WHY THIS EXISTS
 * ---------------
 * sessionStorage can be copied by the browser during duplicate-tab.
 *
 * A copied tab may therefore appear to already own:
 * - the same tabId
 * - the same sessionId
 * - the same userId
 *
 * That is unsafe in the strict tab-selected model.
 *
 * So we store an owner marker in sessionStorage and compare it with
 * a fresh in-memory owner marker created for the CURRENT JS runtime.
 *
 * If they differ, this runtime knows:
 *   "I inherited sessionStorage from another runtime"
 *
 * and must strip inherited local authority.
 */
const SS_TAB_RUNTIME_OWNER = "hb_tab_runtime_owner";

/* -------------------------------------------------------------------------- */
/* Runtime-only owner marker                                                  */
/* -------------------------------------------------------------------------- */

/**
 * This value exists only in memory for the current JS runtime.
 *
 * Key property:
 * - page reload in the SAME tab => new runtime owner is created,
 *   but sessionStorage belongs to the same tab and should be preserved
 *
 * - duplicate tab => new runtime owner is created AND sessionStorage may be copied,
 *   which we must detect and repair
 *
 * Why reload is still okay:
 * - after reload, sessionStorage belongs to the same tab instance in normal flow
 * - the old runtime is gone, but the new runtime can safely take ownership
 * - we only treat it as a clone when storage was copied from another live runtime
 *   pattern and must repair the duplicated inherited auth state
 *
 * In practice, for your required hardening, if a copied sessionStorage appears
 * under a new runtime, we prefer security over convenience and strip inherited
 * local session authority.
 */
const TAB_RUNTIME_OWNER = generateId("owner");

/* -------------------------------------------------------------------------- */
/* Safe storage helpers                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Storage access must be exception-safe because browsers may reject storage
 * access in some edge cases.
 */

function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    // ignore storage failures
  }
}

function safeRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    // ignore storage failures
  }
}

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

function normalizeString(value) {
  const s = String(value || "").trim();
  return s || "";
}

function generateId(prefix = "id") {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `${prefix}_${crypto.randomUUID()}`;
    }
  } catch {
    // ignore and use fallback
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/* -------------------------------------------------------------------------- */
/* Duplicate-tab repair helpers                                               */
/* -------------------------------------------------------------------------- */

/**
 * Strip locally inherited session authority.
 *
 * This is the critical hardening step for duplicated tabs.
 *
 * IMPORTANT:
 * - removes only tab-local selector/user metadata
 * - does NOT call backend
 * - does NOT touch cookies
 * - does NOT affect sibling tabs
 */
function stripInheritedSessionAuthority() {
  const inheritedSessionId = normalizeString(
    safeGet(sessionStorage, SS_SESSION_ID)
  );
  const inheritedUserId = normalizeString(
    safeGet(sessionStorage, SS_USER_ID)
  );

  safeRemove(sessionStorage, SS_SESSION_ID);
  safeRemove(sessionStorage, SS_USER_ID);

  if (inheritedSessionId || inheritedUserId) {
    dwarn("stripped inherited session authority from cloned tab", {
      inheritedSessionId: shortId(inheritedSessionId),
      inheritedUserId: inheritedUserId || "",
    });
  }
}

/**
 * Ensure THIS runtime owns a valid diagnostic tab identity.
 *
 * IMPORTANT BEHAVIOR
 * ------------------
 * We distinguish three cases:
 *
 * 1) Healthy same-tab continuation
 *    - storage already exists
 *    - stored runtime owner matches current runtime owner
 *    - keep tabId
 *
 * 2) Fresh new tab
 *    - no stored tabId
 *    - no stored runtime owner
 *    - generate new tabId and owner
 *
 * 3) Duplicated/cloned tab
 *    - stored runtime owner exists
 *    - stored runtime owner does NOT match current runtime owner
 *    - generate NEW tabId
 *    - overwrite owner with current runtime owner
 *    - strip inherited session/user identity
 *
 * SECURITY PREFERENCE
 * -------------------
 * When in doubt, prefer to clear inherited local auth state.
 * This matches your requirement:
 * duplicate tab must require login again.
 */
function ensureTabIdentity() {
  const storedTabId = normalizeString(safeGet(sessionStorage, SS_TAB_ID));
  const storedOwner = normalizeString(
    safeGet(sessionStorage, SS_TAB_RUNTIME_OWNER)
  );

  /**
   * Case 1:
   * same runtime already owns this storage.
   */
  if (storedTabId && storedOwner && storedOwner === TAB_RUNTIME_OWNER) {
    return storedTabId;
  }

  /**
   * Case 2:
   * completely fresh tab with no stored identity at all.
   */
  if (!storedTabId && !storedOwner) {
    const nextTabId = generateId("tab");

    safeSet(sessionStorage, SS_TAB_ID, nextTabId);
    safeSet(sessionStorage, SS_TAB_RUNTIME_OWNER, TAB_RUNTIME_OWNER);

    dlog("fresh tab identity created", {
      tabId: shortId(nextTabId),
      runtimeOwner: shortId(TAB_RUNTIME_OWNER),
    });

    return nextTabId;
  }

  /**
   * Case 3:
   * storage exists but belongs to another runtime.
   *
   * This strongly indicates duplicated / cloned sessionStorage.
   *
   * Required behavior:
   * - do NOT trust inherited session/user identity
   * - assign a new diagnostic tab id
   * - take ownership with the current runtime owner
   */
  const previousTabId = storedTabId;
  const nextTabId = generateId("tab");

  safeSet(sessionStorage, SS_TAB_ID, nextTabId);
  safeSet(sessionStorage, SS_TAB_RUNTIME_OWNER, TAB_RUNTIME_OWNER);

  stripInheritedSessionAuthority();

  dwarn("detected cloned/duplicated tab storage; repaired tab identity", {
    previousTabId: shortId(previousTabId),
    nextTabId: shortId(nextTabId),
    previousOwner: shortId(storedOwner),
    nextOwner: shortId(TAB_RUNTIME_OWNER),
  });

  return nextTabId;
}

/* -------------------------------------------------------------------------- */
/* Public diagnostics API                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Return diagnostic tab id for THIS browser tab/runtime.
 *
 * This call also ensures duplicate-tab repair has already happened.
 */
export function getTabId() {
  return ensureTabIdentity();
}

/**
 * Return the current tab-local session snapshot.
 *
 * This is the main read model used by:
 * - AuthContext
 * - apiClient
 * - diagnostics
 * - socket client
 *
 * The snapshot is intentionally minimal.
 *
 * IMPORTANT:
 * Calling this also ensures duplicate-tab repair is applied first.
 */
export function getCurrentSessionSnapshot() {
  const snapshot = {
    sessionId: normalizeString(safeGet(sessionStorage, SS_SESSION_ID)),
    userId: normalizeString(safeGet(sessionStorage, SS_USER_ID)),
    tabId: ensureTabIdentity(),
  };

  dlog("current session snapshot read", {
    sessionId: shortId(snapshot.sessionId),
    userId: snapshot.userId || "",
    tabId: shortId(snapshot.tabId),
  });

  return snapshot;
}

/**
 * True when THIS TAB currently owns an explicit local session selector.
 *
 * This is useful for:
 * - AuthContext logic
 * - diagnostics
 * - explicit "is this tab session-routable?" checks
 */
export function hasTabSessionIdentity() {
  ensureTabIdentity();

  const hasIdentity = Boolean(
    normalizeString(safeGet(sessionStorage, SS_SESSION_ID))
  );

  dlog("hasTabSessionIdentity evaluated", {
    hasIdentity,
    tabId: shortId(getTabId()),
  });

  return hasIdentity;
}

/* -------------------------------------------------------------------------- */
/* Session write API                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Store tab-local session selector and optional user id.
 *
 * IMPORTANT:
 * - affects ONLY THIS TAB
 * - does NOT touch any sibling tabs
 * - does NOT write to localStorage
 * - does NOT call backend
 *
 * sessionId is required because this helper should never create partial
 * local authority accidentally.
 *
 * IMPORTANT HARDENING NOTE
 * ------------------------
 * We ensure tab identity first, so a duplicated tab is repaired before any
 * new session authority is stored.
 */
export function setSessionIdentity({ sessionId, userId } = {}) {
  ensureTabIdentity();

  const sid = normalizeString(sessionId);
  const uid = normalizeString(userId);

  if (!sid) {
    dwarn("setSessionIdentity ignored because sessionId is empty");
    return;
  }

  safeSet(sessionStorage, SS_SESSION_ID, sid);

  if (uid) {
    safeSet(sessionStorage, SS_USER_ID, uid);
  } else {
    safeRemove(sessionStorage, SS_USER_ID);
  }

  dlog("session identity stored for this tab", {
    sessionId: shortId(sid),
    userId: uid || "",
    tabId: shortId(getTabId()),
  });
}

/**
 * Convenience wrapper kept because some existing files may still call it.
 */
export function setSessionId(sessionId) {
  setSessionIdentity({ sessionId });
}

/**
 * Clear tab-local session identity.
 *
 * IMPORTANT:
 * - clears ONLY THIS TAB
 * - does NOT log out other tabs
 * - does NOT clear cookies
 * - does NOT call backend logout
 *
 * This is the correct local-collapse primitive for:
 * - refresh failure
 * - auth bootstrap failure
 * - local switch-account preparation
 * - explicit local auth reset
 *
 * IMPORTANT:
 * We do NOT clear tabId or runtime owner.
 *
 * Why?
 * - tab identity is diagnostic/runtime identity, not auth authority
 * - keeping it makes logs easier to correlate
 * - same tab may later log in again and should remain the same logical tab
 */
export function clearTabSessionIdentity() {
  const before = getCurrentSessionSnapshot();

  safeRemove(sessionStorage, SS_SESSION_ID);
  safeRemove(sessionStorage, SS_USER_ID);

  dlog("session identity cleared for this tab", {
    previousSessionId: shortId(before.sessionId),
    previousUserId: before.userId || "",
    tabId: shortId(before.tabId),
  });
}

/**
 * Backward-compatible alias.
 */
export function clearSessionId() {
  clearTabSessionIdentity();
}

/* -------------------------------------------------------------------------- */
/* Session read API                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Return current tab-local session id only.
 *
 * IMPORTANT:
 * The optional argument is accepted only for compatibility with older call sites.
 * It is intentionally ignored.
 *
 * In the hardened strict tab-selected model:
 * - if THIS tab has a local session id, return it
 * - otherwise return empty string
 *
 * There is:
 * - no rehydrate
 * - no registry lookup
 * - no cross-tab recovery
 * - no fallback guessing
 *
 * IMPORTANT:
 * We ensure tab identity first, so cloned tabs are repaired before session
 * authority is read.
 */
export function getSessionId(_options = {}) {
  ensureTabIdentity();

  const current = normalizeString(safeGet(sessionStorage, SS_SESSION_ID));

  if (current) {
    dlog("getSessionId returned tab-local session id", {
      sessionId: shortId(current),
      tabId: shortId(getTabId()),
    });
    return current;
  }

  dlog("getSessionId returned empty (no local tab session id)", {
    tabId: shortId(getTabId()),
  });

  return "";
}